import { RecoverableNetworkError, retryWithBackoff } from '@canvas/retry';

import { PLACEMENT_ID } from './messages.ts';
import { PATHNAME } from './provider.tsx';
import { type MessageProps } from './use-changeboarding-messages.tsx';

// Typical retriable status codes - rate limit, or a 5xx. 5xx is a simple
// inequality condition, only including 429 here for now and we'll add more as
// we require.
const RETRIABLE_STATUS_CODES = new Set([429]);

const DEFAULT_LOCALE = 'en';
const PRODUCT = 'confluence-whiteboards';

/**
 * Fetches the placement props from the server. We don't catch errors in here as
 * they are bubbling into a retryWithBackoff callback. We also use the IDB cache
 * to store the placement props and only fetch them again if they are expired.
 * @returns The placement props
 */
export const getPOPlacementProps = async () =>
  retryWithBackoff({
    callback: async (): Promise<{
      messages: MessageProps[];
    }> => {
      const response = await fetch(
        `${PATHNAME}${PLACEMENT_ID}?locale=${DEFAULT_LOCALE}&product=${PRODUCT}`,
      );
      if (!response.ok) {
        if (RETRIABLE_STATUS_CODES.has(response.status) || response.status >= 500) {
          throw new RecoverableNetworkError('Failed to fetch placement props');
        }

        throw new Error('Failed to fetch placement props');
      }

      return await response.json();
    },
    maxRetries: 3,
    shouldRetry: (error) => error instanceof RecoverableNetworkError,
  });
